import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { motion, useAnimationFrame, useInView, useReducedMotion } from "framer-motion";
import { useTheme } from "../ThemeContext";

/**
 * Animated illustrations for the "value" cards. Each one tells a small story:
 *  - Churn: retention keeps climbing while a typical program's patients drop off
 *  - No-shows: a risk scanner sweeps a schedule and nudges the slots at risk
 *  - Outreach: touchpoints pulse out from the clinic to patients
 *  - Medication: a dose ring fills up, reminders rescue the missed doses
 *  - Care plan: a traveler moves along the route, lighting each milestone
 * They only render (and animate) while on screen, and settle into a static frame for reduced motion.
 */

const PURPLE = "#a855f7";
const INDIGO = "#6366f1";
const CYAN = "#22d3ee";
const GREEN = "#10b981";
const AMBER = "#f59e0b";
const RED = "#ef4444";
const PINK = "#f472b6";

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

/** Mounts children only while the box is near the viewport, so loops don't run off-screen. */
export function Stage({ children, className = "" }: { children: ReactNode; className?: string }) {
    const ref = useRef<HTMLDivElement>(null);
    const near = useInView(ref, { margin: "150px" });
    return (
        <div ref={ref} className={`relative ${className}`}>
            {near && children}
        </div>
    );
}

/** Shared soft glow + dotted texture behind every graphic. */
export function GraphicBackdrop({ tint }: { tint: string }) {
    return (
        <div className="pointer-events-none absolute inset-0">
            <div className="absolute -top-16 left-1/2 h-48 w-72 -translate-x-1/2 rounded-full opacity-40 blur-3xl dark:opacity-30" style={{ background: tint }} />
            <div
                className="absolute inset-0 opacity-[0.35] dark:opacity-25"
                style={{
                    backgroundImage: "radial-gradient(currentColor 1px, transparent 1px)",
                    backgroundSize: "16px 16px",
                    color: "rgb(148 163 184 / 0.5)",
                    maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
                    WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
                }}
            />
        </div>
    );
}

/** Runs `frame(phase 0-1)` every animation frame; renders one settled frame when motion is reduced. */
function useCycle(periodMs: number, frame: (phase: number) => void, settledPhase: number) {
    const reduce = useReducedMotion();
    useLayoutEffect(() => {
        if (reduce) frame(settledPhase);
    }); // eslint-disable-line react-hooks/exhaustive-deps
    useAnimationFrame((t) => {
        if (!reduce) frame((t % periodMs) / periodMs);
    });
}

/* ------------------------------------------------------------------ */
/* 1. Churn: retention vs. drop-off                                    */
/* ------------------------------------------------------------------ */

/** Scales the x coordinates of "x y" pairs in a path, keeping the chart origin at x=40. */
const stretchPath = (d: string, k: number) =>
    d.replace(/(-?\d+\.?\d*) (-?\d+\.?\d*)/g, (_, x: string, y: string) => `${40 + (parseFloat(x) - 40) * k} ${y}`);

function useWide(query = "(min-width: 768px)") {
    const [wide, setWide] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia(query);
        const update = () => setWide(mq.matches);
        update();
        mq.addEventListener("change", update);
        return () => mq.removeEventListener("change", update);
    }, [query]);
    return wide;
}

const QUILT_PATH = "M40 62 C120 58 200 52 280 46 S390 38 440 34";
const TYPICAL_PATH = "M40 62 C100 74 150 128 215 150 S345 186 440 194";
// Points sitting on the typical curve, where a patient "drops off"
const DROP_POINTS = [
    { x: 125, y: 102 },
    { x: 215, y: 150 },
    { x: 264, y: 165 },
    { x: 316, y: 177 },
    { x: 374, y: 187 },
];
const STAY_POINTS = [
    { x: 160, y: 55 },
    { x: 280, y: 46 },
    { x: 360, y: 40 },
];

export function ChurnGraphic() {
    const { theme } = useTheme();
    const dark = theme === "dark";
    const id = useId().replace(/:/g, "");
    const quilt = useRef<SVGPathElement>(null);
    const glow = useRef<SVGPathElement>(null);
    const typical = useRef<SVGPathElement>(null);
    const area = useRef<SVGPathElement>(null);
    const marker = useRef<SVGGElement>(null);
    const drops = useRef<(SVGGElement | null)[]>([]);
    const stays = useRef<(SVGCircleElement | null)[]>([]);
    const root = useRef<SVGGElement>(null);
    const lengths = useRef({ q: 0, t: 0 });
    // The churn card is double width from tablet up, so the chart stretches to fill it
    const wide = useWide();
    const K = wide ? 1.6 : 1;
    const sx = (x: number) => 40 + (x - 40) * K;
    const quiltD = stretchPath(QUILT_PATH, K);
    const typicalD = stretchPath(TYPICAL_PATH, K);

    useLayoutEffect(() => {
        if (quilt.current && typical.current) {
            lengths.current = { q: quilt.current.getTotalLength(), t: typical.current.getTotalLength() };
        }
    }, [K]);

    useCycle(7000, (phase) => {
        const f = clamp01(phase / 0.5);
        const fade = phase > 0.9 ? clamp01((1 - phase) / 0.1) : 1;
        const { q, t } = lengths.current;
        if (!quilt.current || !typical.current || !area.current || !marker.current || !root.current) return;
        root.current.style.opacity = String(fade);
        quilt.current.style.strokeDasharray = `${q}`;
        quilt.current.style.strokeDashoffset = `${q * (1 - f)}`;
        if (glow.current) {
            glow.current.style.strokeDasharray = `${q}`;
            glow.current.style.strokeDashoffset = `${q * (1 - f)}`;
        }
        typical.current.style.strokeDasharray = `${t}`;
        typical.current.style.strokeDashoffset = `${t * (1 - f)}`;
        area.current.style.opacity = String(f * 0.9);
        const p = quilt.current.getPointAtLength(q * f);
        marker.current.setAttribute("transform", `translate(${p.x} ${p.y})`);
        marker.current.style.opacity = f > 0.02 ? "1" : "0";
        DROP_POINTS.forEach((d, i) => {
            const g = drops.current[i];
            if (!g) return;
            const at = (d.x - 40) / 400;
            const fall = clamp01((f - at) / 0.14);
            g.style.opacity = f >= at ? String(1 - fall) : "0";
            g.setAttribute("transform", `translate(0 ${fall * 30})`);
        });
        STAY_POINTS.forEach((d, i) => {
            const c = stays.current[i];
            if (!c) return;
            const at = (d.x - 40) / 400;
            c.style.opacity = f >= at ? "1" : "0";
            c.setAttribute("r", String(f >= at ? 3.5 + Math.max(0, 1 - (f - at) / 0.1) * 3 : 0));
        });
    }, 0.5);

    return (
        <svg viewBox={`0 0 ${sx(440) + 40} 230`} className="h-full w-full" aria-hidden="true">
            <defs>
                <linearGradient id={`${id}-line`} x1="0" x2="1" y1="0" y2="0">
                    <stop offset="0" stopColor={PURPLE} />
                    <stop offset="0.6" stopColor={INDIGO} />
                    <stop offset="1" stopColor={CYAN} />
                </linearGradient>
                <linearGradient id={`${id}-area`} x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0" stopColor={PURPLE} stopOpacity="0.35" />
                    <stop offset="1" stopColor={PURPLE} stopOpacity="0" />
                </linearGradient>
                <filter id={`${id}-glow`} x="-20%" y="-50%" width="140%" height="200%">
                    <feGaussianBlur stdDeviation="4" />
                </filter>
            </defs>

            {/* grid */}
            {[40, 90, 140, 190].map((y) => (
                <line key={y} x1="40" x2={sx(440)} y1={y} y2={y} className="stroke-slate-300/60 dark:stroke-white/10" strokeDasharray="2 6" />
            ))}
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                <line key={i} x1={sx(40 + i * 57)} x2={sx(40 + i * 57)} y1="30" y2="200" className="stroke-slate-300/40 dark:stroke-white/5" />
            ))}
            <text x="40" y="218" className="fill-slate-400 dark:fill-slate-500" fontSize="10">Week 1</text>
            <text x={sx(440)} y="218" textAnchor="end" className="fill-slate-400 dark:fill-slate-500" fontSize="10">Week 8</text>

            <g ref={root}>
                <path ref={area} d={`${quiltD} L${sx(440)} 200 L40 200 Z`} fill={`url(#${id}-area)`} opacity="0" />
                {/* typical program: patients leave */}
                <path ref={typical} d={typicalD} fill="none" stroke={RED} strokeOpacity="0.85" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="1 0" />
                {/* with Quilt: glow + crisp line */}
                <path ref={glow} d={quiltD} fill="none" stroke={`url(#${id}-line)`} strokeWidth="9" opacity="0.35" filter={`url(#${id}-glow)`} />
                <path ref={quilt} d={quiltD} fill="none" stroke={`url(#${id}-line)`} strokeWidth="4" strokeLinecap="round" />

                {DROP_POINTS.map((d, i) => (
                    <g key={i} ref={(el) => { drops.current[i] = el; }} opacity="0">
                        <circle cx={sx(d.x)} cy={d.y} r="4.5" fill={dark ? "#475569" : "#94a3b8"} stroke={RED} strokeWidth="1.5" />
                    </g>
                ))}
                {STAY_POINTS.map((d, i) => (
                    <circle key={i} ref={(el) => { stays.current[i] = el; }} cx={sx(d.x)} cy={d.y} r="0" fill="#fff" stroke={PURPLE} strokeWidth="2.5" opacity="0" />
                ))}

                <g ref={marker} opacity="0">
                    <circle r="14" fill={CYAN} opacity="0.2" />
                    <circle r="7" fill="#fff" stroke={CYAN} strokeWidth="3" />
                </g>
            </g>

            {/* legend */}
            <g fontSize="11" fontWeight="600">
                <circle cx="52" cy="14" r="4" fill={PURPLE} />
                <text x="62" y="18" className="fill-slate-600 dark:fill-slate-300">With Quilt</text>
                <circle cx="150" cy="14" r="4" fill={RED} />
                <text x="160" y="18" className="fill-slate-600 dark:fill-slate-300">Typical drop-off</text>
            </g>
        </svg>
    );
}

/* ------------------------------------------------------------------ */
/* 2. No-shows: risk scanner                                            */
/* ------------------------------------------------------------------ */

const DAYS = ["Mon", "Tue", "Wed", "Thu"];
const SLOT_W = 56;
const SLOT_H = 38;
const slotX = (c: number) => 22 + c * 68;
const slotY = (r: number) => 42 + r * 52;
const AT_RISK = new Set(["0-2", "1-0", "1-3", "2-2"]);
const CYCLE_D = 7;

function RiskSlot({ r, c, risky, dark }: { r: number; c: number; risky: boolean; dark: boolean }) {
    const reduce = useReducedMotion();
    const x = slotX(c);
    const y = slotY(r);
    const cx = x + SLOT_W / 2;
    const base = dark ? "rgba(148,163,184,0.14)" : "rgba(148,163,184,0.18)";
    const baseStroke = dark ? "rgba(148,163,184,0.25)" : "rgba(100,116,139,0.3)";

    if (!risky) {
        return <rect x={x} y={y} width={SLOT_W} height={SLOT_H} rx="10" fill={base} stroke={baseStroke} />;
    }

    const pass = (cx / 290) * 0.45; // when the scanner reaches this column (fraction of the cycle)
    const times = [0, pass, pass + 0.04, pass + 0.22, 0.92, 1];
    const transition = { duration: CYCLE_D, times, repeat: Infinity, ease: "linear" as const };
    const checkTimes = [0, pass + 0.2, pass + 0.28, 0.92, 1];
    const bubbleTimes = [0, pass + 0.04, pass + 0.09, pass + 0.18, pass + 0.22, 1];

    if (reduce) {
        return (
            <g>
                <rect x={x} y={y} width={SLOT_W} height={SLOT_H} rx="10" fill="rgba(16,185,129,0.25)" stroke={GREEN} />
                <path d={`M${cx - 8} ${y + 19} l6 6 l11 -12`} fill="none" stroke={GREEN} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </g>
        );
    }

    return (
        <g>
            <motion.rect
                x={x}
                y={y}
                width={SLOT_W}
                height={SLOT_H}
                rx="10"
                initial={{ fill: base, stroke: baseStroke }}
                animate={{
                    fill: [base, base, "rgba(245,158,11,0.35)", "rgba(16,185,129,0.25)", "rgba(16,185,129,0.25)", base],
                    stroke: [baseStroke, baseStroke, AMBER, GREEN, GREEN, baseStroke],
                }}
                transition={transition}
            />
            <motion.path
                d={`M${cx - 8} ${y + 19} l6 6 l11 -12`}
                fill="none"
                stroke={GREEN}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: [0, 0, 1, 1, 0] }}
                transition={{ duration: CYCLE_D, times: checkTimes, repeat: Infinity, ease: "easeOut" }}
            />
            {/* nudge bubble */}
            <motion.g
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: [0, 0, 1, 1, 0, 0], scale: [0.6, 0.6, 1, 1, 0.8, 0.6], y: [4, 4, 0, 0, -6, 4] }}
                transition={{ duration: CYCLE_D, times: bubbleTimes, repeat: Infinity, ease: "easeOut" }}
                style={{ transformBox: "fill-box", transformOrigin: "50% 100%" }}
            >
                <rect x={cx - 24} y={y - 22} width="48" height="18" rx="9" fill={PURPLE} />
                <path d={`M${cx - 4} ${y - 4} l4 5 l4 -5z`} fill={PURPLE} />
                <text x={cx} y={y - 9.5} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#fff">Nudge</text>
            </motion.g>
        </g>
    );
}

export function NoShowGraphic() {
    const { theme } = useTheme();
    const dark = theme === "dark";
    const reduce = useReducedMotion();
    const id = useId().replace(/:/g, "");

    return (
        <svg viewBox="0 0 300 230" className="h-full w-full" aria-hidden="true">
            <defs>
                <linearGradient id={`${id}-scan`} x1="0" x2="1" y1="0" y2="0">
                    <stop offset="0" stopColor={PURPLE} stopOpacity="0" />
                    <stop offset="1" stopColor={PURPLE} stopOpacity="0.55" />
                </linearGradient>
            </defs>
            {DAYS.map((d, c) => (
                <text key={d} x={slotX(c) + SLOT_W / 2} y="30" textAnchor="middle" fontSize="10" fontWeight="600" className="fill-slate-400 dark:fill-slate-500">{d}</text>
            ))}
            {[0, 1, 2].map((r) => [0, 1, 2, 3].map((c) => (
                <RiskSlot key={`${r}-${c}`} r={r} c={c} risky={AT_RISK.has(`${r}-${c}`)} dark={dark} />
            )))}

            {!reduce && (
                <motion.g
                    initial={{ x: 0, opacity: 0 }}
                    animate={{ x: [0, 290, 290], opacity: [1, 1, 0] }}
                    transition={{ duration: CYCLE_D, times: [0, 0.45, 0.5], repeat: Infinity, ease: "linear" }}
                >
                    <rect x="-50" y="34" width="50" height="168" fill={`url(#${id}-scan)`} />
                    <rect x="-1.5" y="34" width="3" height="168" rx="1.5" fill={PURPLE} />
                </motion.g>
            )}
        </svg>
    );
}

/* ------------------------------------------------------------------ */
/* 3. Outreach: touchpoints from the clinic                             */
/* ------------------------------------------------------------------ */

const HUB = { x: 150, y: 112 };
const polar = (cx: number, cy: number, r: number, deg: number) => {
    const a = (deg * Math.PI) / 180;
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
};
const OUTREACH_NODES = [
    { r: 52, a: 15, color: "#38bdf8" },
    { r: 52, a: 135, color: PINK },
    { r: 52, a: 255, color: "#34d399" },
    { r: 88, a: 75, color: AMBER },
    { r: 88, a: 195, color: "#a78bfa" },
    { r: 88, a: 315, color: "#60a5fa" },
].map((n) => ({ ...n, ...polar(HUB.x, HUB.y, n.r, n.a) }));

export function OutreachGraphic() {
    const reduce = useReducedMotion();
    const id = useId().replace(/:/g, "");

    return (
        <svg viewBox="0 0 300 230" className="h-full w-full" aria-hidden="true">
            <defs>
                <radialGradient id={`${id}-hub`}>
                    <stop offset="0" stopColor="#c084fc" />
                    <stop offset="1" stopColor={INDIGO} />
                </radialGradient>
                <linearGradient id={`${id}-line`} x1="0" x2="1" y1="0" y2="0">
                    <stop offset="0" stopColor={PURPLE} stopOpacity="0.5" />
                    <stop offset="1" stopColor={CYAN} stopOpacity="0.15" />
                </linearGradient>
            </defs>

            {[52, 88].map((r) => (
                <circle key={r} cx={HUB.x} cy={HUB.y} r={r} fill="none" className="stroke-slate-300/70 dark:stroke-white/10" strokeDasharray="3 6" />
            ))}

            {/* ripples from the clinic */}
            {!reduce && [0, 1.6].map((delay) => (
                <motion.circle
                    key={delay}
                    cx={HUB.x}
                    cy={HUB.y}
                    fill="none"
                    stroke={PURPLE}
                    strokeWidth="1.5"
                    initial={{ r: 18, opacity: 0.5 }}
                    animate={{ r: [18, 104], opacity: [0.55, 0] }}
                    transition={{ duration: 3.2, delay, repeat: Infinity, ease: "easeOut" }}
                />
            ))}

            <g>
                {!reduce && <animateTransform attributeName="transform" type="rotate" from={`0 ${HUB.x} ${HUB.y}`} to={`360 ${HUB.x} ${HUB.y}`} dur="70s" repeatCount="indefinite" />}
                {OUTREACH_NODES.map((n, i) => (
                    <g key={i}>
                        <line x1={HUB.x} y1={HUB.y} x2={n.x} y2={n.y} stroke={`url(#${id}-line)`} strokeWidth="1.5" />
                        {!reduce && (
                            <circle r="3" fill="#fff" opacity="0">
                                <animateMotion path={`M${HUB.x} ${HUB.y} L${n.x} ${n.y}`} dur="2.4s" begin={`${i * 0.45}s`} repeatCount="indefinite" />
                                <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.15;0.8;1" dur="2.4s" begin={`${i * 0.45}s`} repeatCount="indefinite" />
                            </circle>
                        )}
                        <circle cx={n.x} cy={n.y} r="13" fill={n.color} opacity="0.15" />
                        <circle cx={n.x} cy={n.y} r="6.5" fill={n.color} />
                        <circle cx={n.x} cy={n.y} r="2.5" fill="#fff" opacity="0.9" />
                        {/* billable check pops when a touchpoint lands */}
                        <motion.g
                            initial={{ scale: reduce ? 1 : 0, opacity: reduce ? 1 : 0 }}
                            animate={reduce ? undefined : { scale: [0, 0, 1.15, 1, 1, 0], opacity: [0, 0, 1, 1, 1, 0] }}
                            transition={{ duration: 4.8, delay: i * 0.45, times: [0, 0.45, 0.52, 0.58, 0.9, 1], repeat: Infinity }}
                            style={{ transformBox: "fill-box", transformOrigin: "center" }}
                        >
                            <circle cx={n.x + 9} cy={n.y - 9} r="6.5" fill={GREEN} />
                            <path d={`M${n.x + 6} ${n.y - 9} l2.2 2.4 l4 -4.6`} fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </motion.g>
                    </g>
                ))}
            </g>

            {/* clinic hub */}
            <circle cx={HUB.x} cy={HUB.y} r="26" fill={PURPLE} opacity="0.18" />
            <circle cx={HUB.x} cy={HUB.y} r="18" fill={`url(#${id}-hub)`} />
            <path d={`M${HUB.x - 7} ${HUB.y} h14 M${HUB.x} ${HUB.y - 7} v14`} stroke="#fff" strokeWidth="3.5" strokeLinecap="round" />
        </svg>
    );
}

/* ------------------------------------------------------------------ */
/* 4. Medication adherence: dose ring                                   */
/* ------------------------------------------------------------------ */

const RING = { x: 150, y: 112, r: 70 };
const SEGMENTS = 14;
const arc = (a0: number, a1: number) => {
    const s = polar(RING.x, RING.y, RING.r, a0);
    const e = polar(RING.x, RING.y, RING.r, a1);
    return `M${s.x} ${s.y} A${RING.r} ${RING.r} 0 0 1 ${e.x} ${e.y}`;
};
const REMINDED = new Set([4, 9]);

function DoseSegment({ i, reduce }: { i: number; reduce: boolean | null }) {
    const step = 360 / SEGMENTS;
    const a0 = -90 + i * step + 2.5;
    const a1 = a0 + step - 5;
    const d = arc(a0, a1);
    const start = 0.06 + i * 0.045;
    const reminded = REMINDED.has(i);
    const mid = polar(RING.x, RING.y, RING.r, (a0 + a1) / 2);
    const color = `hsl(${268 - i * 6} 85% ${62}%)`;
    const D = 8;

    if (reduce) {
        return <path d={d} fill="none" stroke={reminded ? GREEN : color} strokeWidth="10" strokeLinecap="round" />;
    }
    return (
        <g>
            {reminded && (
                <>
                    <motion.path
                        d={d}
                        fill="none"
                        stroke={AMBER}
                        strokeWidth="10"
                        strokeLinecap="round"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: [0, 0, 1, 1, 0, 0] }}
                        transition={{ duration: D, times: [0, start - 0.1, start - 0.07, start + 0.02, start + 0.05, 1], repeat: Infinity }}
                    />
                    <motion.circle
                        cx={mid.x}
                        cy={mid.y}
                        fill="none"
                        stroke={AMBER}
                        strokeWidth="2"
                        initial={{ r: 6, opacity: 0 }}
                        animate={{ r: [6, 6, 24], opacity: [0, 0.9, 0] }}
                        transition={{ duration: 1.1, delay: (start - 0.07) * D, repeat: Infinity, repeatDelay: D - 1.1, times: [0, 0.01, 1], ease: "easeOut" }}
                    />
                </>
            )}
            <motion.path
                d={d}
                fill="none"
                stroke={reminded ? GREEN : color}
                strokeWidth="10"
                strokeLinecap="round"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0, 1, 1, 0, 0] }}
                transition={{ duration: D, times: [0, start, start + 0.03, 0.9, 0.97, 1], repeat: Infinity }}
            />
        </g>
    );
}

export function MedicationGraphic() {
    const reduce = useReducedMotion();
    const id = useId().replace(/:/g, "");

    return (
        <svg viewBox="0 0 300 230" className="h-full w-full" aria-hidden="true">
            <defs>
                <linearGradient id={`${id}-cap`} x1="0" x2="1" y1="0" y2="0">
                    <stop offset="0" stopColor={PURPLE} />
                    <stop offset="1" stopColor={INDIGO} />
                </linearGradient>
            </defs>
            {Array.from({ length: SEGMENTS }).map((_, i) => {
                const step = 360 / SEGMENTS;
                const a0 = -90 + i * step + 2.5;
                return <path key={i} d={arc(a0, a0 + step - 5)} fill="none" className="stroke-slate-300/70 dark:stroke-white/10" strokeWidth="10" strokeLinecap="round" />;
            })}
            {Array.from({ length: SEGMENTS }).map((_, i) => <DoseSegment key={i} i={i} reduce={reduce} />)}

            {/* twinkles */}
            {!reduce && [[96, 66], [208, 74], [102, 158], [204, 150]].map(([x, y], i) => (
                <motion.path
                    key={i}
                    d={`M${x} ${y - 5} L${x + 1.5} ${y - 1.5} L${x + 5} ${y} L${x + 1.5} ${y + 1.5} L${x} ${y + 5} L${x - 1.5} ${y + 1.5} L${x - 5} ${y} L${x - 1.5} ${y - 1.5}Z`}
                    fill={i % 2 ? CYAN : PINK}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: [0, 1, 0], scale: [0, 1, 0] }}
                    transition={{ duration: 2.4, delay: i * 0.6, repeat: Infinity, ease: "easeInOut" }}
                    style={{ transformBox: "fill-box", transformOrigin: "center" }}
                />
            ))}

            {/* capsule */}
            <motion.g
                animate={reduce ? undefined : { y: [-3, 3, -3], rotate: [-38, -32, -38] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                style={{ transformBox: "fill-box", transformOrigin: "center" }}
            >
                <g transform={`rotate(-35 ${RING.x} ${RING.y})`}>
                    <rect x={RING.x - 24} y={RING.y - 11} width="48" height="22" rx="11" fill={`url(#${id}-cap)`} />
                    <path d={`M${RING.x} ${RING.y - 11} h13 a11 11 0 0 1 0 22 h-13z`} fill="#fff" opacity="0.92" />
                    <rect x={RING.x - 19} y={RING.y - 7} width="12" height="3" rx="1.5" fill="#fff" opacity="0.5" />
                </g>
            </motion.g>
        </svg>
    );
}

/* ------------------------------------------------------------------ */
/* 5. Care plan: milestone route                                        */
/* ------------------------------------------------------------------ */

const ROUTE = "M24 184 C80 184 70 118 130 118 S196 178 238 136 S262 72 280 46";
const MILESTONES = 5;

export function CarePlanGraphic() {
    const { theme } = useTheme();
    const dark = theme === "dark";
    const id = useId().replace(/:/g, "");
    const route = useRef<SVGPathElement>(null);
    const trail = useRef<SVGPathElement>(null);
    const glowTrail = useRef<SVGPathElement>(null);
    const traveler = useRef<SVGGElement>(null);
    const nodes = useRef<(SVGGElement | null)[]>([]);
    const rings = useRef<(SVGCircleElement | null)[]>([]);
    const root = useRef<SVGGElement>(null);
    const [points, setPoints] = useState<{ x: number; y: number }[]>([]);
    const len = useRef(0);

    useEffect(() => {
        const p = route.current;
        if (!p) return;
        len.current = p.getTotalLength();
        setPoints(Array.from({ length: MILESTONES }, (_, i) => {
            const pt = p.getPointAtLength((len.current * i) / (MILESTONES - 1));
            return { x: pt.x, y: pt.y };
        }));
    }, []);

    useCycle(8000, (phase) => {
        const route_ = route.current;
        if (!route_ || !trail.current || !traveler.current || !root.current || !len.current) return;
        const f = clamp01(phase / 0.72);
        const fade = phase > 0.92 ? clamp01((1 - phase) / 0.08) : 1;
        root.current.style.opacity = String(fade);
        trail.current.style.strokeDasharray = `${len.current}`;
        trail.current.style.strokeDashoffset = `${len.current * (1 - f)}`;
        if (glowTrail.current) {
            glowTrail.current.style.strokeDasharray = `${len.current}`;
            glowTrail.current.style.strokeDashoffset = `${len.current * (1 - f)}`;
        }
        const p = route_.getPointAtLength(len.current * f);
        traveler.current.setAttribute("transform", `translate(${p.x} ${p.y})`);
        traveler.current.style.opacity = phase < 0.8 ? "1" : String(clamp01((0.86 - phase) / 0.06));
        for (let i = 0; i < MILESTONES; i++) {
            const at = i / (MILESTONES - 1);
            const lit = f >= at - 0.001;
            const g = nodes.current[i];
            const ring = rings.current[i];
            if (g) g.style.opacity = lit ? "1" : "0.35";
            if (g) g.setAttribute("data-lit", lit ? "1" : "0");
            if (ring) {
                const since = (f - at) / 0.1;
                ring.setAttribute("r", String(lit && since < 1 ? 8 + since * 16 : 0));
                ring.style.opacity = lit && since < 1 ? String(0.7 * (1 - since)) : "0";
            }
        }
    }, 0.8);

    return (
        <svg viewBox="0 0 300 230" className="h-full w-full" aria-hidden="true">
            <defs>
                <linearGradient id={`${id}-trail`} x1="0" x2="1" y1="1" y2="0">
                    <stop offset="0" stopColor={PURPLE} />
                    <stop offset="1" stopColor={CYAN} />
                </linearGradient>
            </defs>

            {/* topographic lines */}
            {[0, 1, 2].map((i) => (
                <path key={i} d={`M0 ${70 + i * 55} C70 ${40 + i * 55} 130 ${100 + i * 55} 300 ${64 + i * 55}`} fill="none" className="stroke-slate-300/50 dark:stroke-white/5" />
            ))}

            <path ref={route} d={ROUTE} fill="none" className="stroke-slate-300 dark:stroke-white/15" strokeWidth="3" strokeDasharray="2 7" strokeLinecap="round" />
            <g ref={root}>
                <path ref={glowTrail} d={ROUTE} fill="none" stroke={`url(#${id}-trail)`} strokeWidth="9" opacity="0.2" strokeLinecap="round" />
                <path ref={trail} d={ROUTE} fill="none" stroke={`url(#${id}-trail)`} strokeWidth="4" strokeLinecap="round" />

                {points.map((p, i) => {
                    const last = i === MILESTONES - 1;
                    return (
                        <g key={i}>
                            <circle ref={(el) => { rings.current[i] = el; }} cx={p.x} cy={p.y} r="0" fill="none" stroke={last ? GREEN : CYAN} strokeWidth="2" opacity="0" />
                            <g ref={(el) => { nodes.current[i] = el; }} opacity="0.35">
                                <circle cx={p.x} cy={p.y} r="11" fill={dark ? "#0f172a" : "#fff"} stroke={last ? GREEN : CYAN} strokeWidth="2.5" />
                                {last ? (
                                    <path d={`M${p.x - 3.5} ${p.y + 5} V${p.y - 6} L${p.x + 6} ${p.y - 3} L${p.x - 3.5} ${p.y}`} fill={GREEN} stroke={GREEN} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
                                ) : (
                                    <path d={`M${p.x - 4} ${p.y} l3 3.2 l5.5 -6.4`} fill="none" stroke={CYAN} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                                )}
                            </g>
                        </g>
                    );
                })}

                <g ref={traveler} opacity="0">
                    <circle r="15" fill={PURPLE} opacity="0.22" />
                    <circle r="7" fill="#fff" stroke={PURPLE} strokeWidth="3.5" />
                </g>
            </g>
        </svg>
    );
}
